/**
 * Clock rounding, tolerance, and overtime allocation.
 *
 * These are the rules a payroll clerk would otherwise apply by hand. All
 * functions are pure so they can be unit tested without a database.
 */

import {
  addDaysToDateKey,
  getWeekStartDateKey,
  getZonedParts,
  MINUTES_PER_DAY,
  roundMoney,
  zonedDateTimeToUtc,
  type TimeInterval,
} from './time';

export type RoundingStrategy = 'none' | 'nearest' | 'up' | 'down';

export interface RoundingRules {
  strategy: RoundingStrategy;
  /** Grid size in minutes, e.g. 15 rounds to :00 :15 :30 :45. */
  intervalMinutes: number;
  /** Treat a punch within the grace window of its planned time as on time. */
  snapToSchedule: boolean;
  clockInGraceMinutes: number;
  clockOutGraceMinutes: number;
}

export interface RoundedClock {
  start: Date;
  end: Date;
  /** Signed minutes the rounding added (+) or removed (-) from paid time. */
  adjustmentMinutes: number;
  snappedStart: boolean;
  snappedEnd: boolean;
}

const MS_PER_MINUTE = 60000;

function roundLocalMinute(minuteOfDay: number, strategy: RoundingStrategy, interval: number): number {
  if (strategy === 'none' || interval <= 0) return minuteOfDay;
  const ratio = minuteOfDay / interval;
  if (strategy === 'up') return Math.ceil(ratio) * interval;
  if (strategy === 'down') return Math.floor(ratio) * interval;
  return Math.round(ratio) * interval;
}

/**
 * Round a punch on the local wall clock rather than the UTC epoch, so a 15
 * minute grid lands on :00/:15/:30/:45 local even in zones whose offset is not
 * a whole hour.
 */
function roundPunch(date: Date, timeZone: string, strategy: RoundingStrategy, interval: number): Date {
  if (strategy === 'none') return date;

  const parts = getZonedParts(date, timeZone);
  // Time-zone offsets change the local hour/minute but not the sub-minute
  // portion of an instant. Retaining it is essential at rounding boundaries:
  // 08:00:00.001 must round up on an "up" rule, while 08:00:00.000 stays put.
  const subMinute = (date.getUTCSeconds() / 60) + (date.getUTCMilliseconds() / MS_PER_MINUTE);
  const minuteOfDay = (parts.hour * 60) + parts.minute + subMinute;
  const rounded = roundLocalMinute(minuteOfDay, strategy, interval);

  // Rounding up from 23:5x can spill into the next local day.
  if (rounded >= MINUTES_PER_DAY) {
    return zonedDateTimeToUtc(addDaysToDateKey(parts.dateKey, 1), rounded - MINUTES_PER_DAY, timeZone);
  }

  return zonedDateTimeToUtc(parts.dateKey, rounded, timeZone);
}

function withinGrace(actual: Date, planned: Date, graceMinutes: number): boolean {
  return Math.abs(actual.getTime() - planned.getTime()) <= graceMinutes * MS_PER_MINUTE;
}

/**
 * Apply schedule snapping then grid rounding to a clocked interval.
 *
 * Snapping runs first and wins: an early arrival inside the grace window is
 * paid from the scheduled start, which is the behaviour that stops guards
 * clocking in 10 minutes early every day to inflate hours.
 */
export function applyRounding(
  clocked: TimeInterval,
  planned: TimeInterval | null,
  timeZone: string,
  rules: RoundingRules,
): RoundedClock {
  let start = clocked.start;
  let end = clocked.end;
  let snappedStart = false;
  let snappedEnd = false;

  if (rules.snapToSchedule && planned) {
    if (withinGrace(start, planned.start, rules.clockInGraceMinutes)) {
      start = planned.start;
      snappedStart = true;
    }
    if (withinGrace(end, planned.end, rules.clockOutGraceMinutes)) {
      end = planned.end;
      snappedEnd = true;
    }
  }

  if (!snappedStart) {
    start = roundPunch(start, timeZone, rules.strategy, rules.intervalMinutes);
  }
  if (!snappedEnd) {
    end = roundPunch(end, timeZone, rules.strategy, rules.intervalMinutes);
  }

  // Rounding must never invert an interval.
  if (end.getTime() < start.getTime()) {
    end = start;
  }

  const originalMinutes = (clocked.end.getTime() - clocked.start.getTime()) / MS_PER_MINUTE;
  const roundedMinutes = (end.getTime() - start.getTime()) / MS_PER_MINUTE;

  return {
    start,
    end,
    adjustmentMinutes: Math.round(roundedMinutes - originalMinutes),
    snappedStart,
    snappedEnd,
  };
}

export interface OvertimeRules {
  enabled: boolean;
  dailyThresholdMinutes: number;
  weeklyThresholdMinutes: number;
  doubleTimeEnabled: boolean;
  doubleTimeDailyThresholdMinutes: number;
  /** 0 = Sunday .. 6 = Saturday. */
  weekStartWeekday: number;
}

export interface OvertimeInput {
  /** Stable identifier so results can be matched back to the source line. */
  id: string;
  /** Local date the work is attributed to. */
  dateKey: string;
  timeZone: string;
  /** Sort key; entries are allocated in chronological order. */
  startedAtMs: number;
  paidMinutes: number;
}

export interface OvertimeAllocation {
  id: string;
  ordinaryMinutes: number;
  /** Overtime created by crossing the daily threshold. */
  dailyOvertimeMinutes: number;
  /** Ordinary time converted after crossing the weekly threshold. */
  weeklyOvertimeMinutes: number;
  /** Total daily and weekly overtime minutes. */
  overtimeMinutes: number;
  doubleTimeMinutes: number;
}

/**
 * Split each entry's paid minutes into ordinary, overtime and double time.
 *
 * Daily thresholds are applied first. Weekly eligibility is based on all
 * worked minutes, but only minutes still classified as ordinary are converted;
 * this reaches the weekly threshold on time without paying one minute twice.
 */
export function allocateOvertime(
  entries: OvertimeInput[],
  rules: OvertimeRules,
): Map<string, OvertimeAllocation> {
  const result = new Map<string, OvertimeAllocation>();

  if (!rules.enabled) {
    entries.forEach((entry) => {
      result.set(entry.id, {
        id: entry.id,
        ordinaryMinutes: entry.paidMinutes,
        dailyOvertimeMinutes: 0,
        weeklyOvertimeMinutes: 0,
        overtimeMinutes: 0,
        doubleTimeMinutes: 0,
      });
    });
    return result;
  }

  const ordered = entries.slice().sort((left, right) => left.startedAtMs - right.startedAtMs);
  const dailyTotals = new Map<string, number>();

  // Pass 1: daily thresholds.
  ordered.forEach((entry) => {
    const priorToday = dailyTotals.get(entry.dateKey) ?? 0;
    const runningTotal = priorToday + entry.paidMinutes;
    dailyTotals.set(entry.dateKey, runningTotal);

    const dailyThreshold = rules.dailyThresholdMinutes;
    const doubleThreshold = rules.doubleTimeEnabled
      ? rules.doubleTimeDailyThresholdMinutes
      : Number.POSITIVE_INFINITY;

    // Minutes of this entry that sit above each threshold.
    const aboveDaily = Math.max(0, Math.min(runningTotal, doubleThreshold) - Math.max(priorToday, dailyThreshold));
    const aboveDouble = Math.max(0, runningTotal - Math.max(priorToday, doubleThreshold));
    const overtimeMinutes = Math.min(aboveDaily, entry.paidMinutes);
    const doubleTimeMinutes = Math.min(aboveDouble, entry.paidMinutes - overtimeMinutes);

    result.set(entry.id, {
      id: entry.id,
      ordinaryMinutes: entry.paidMinutes - overtimeMinutes - doubleTimeMinutes,
      dailyOvertimeMinutes: overtimeMinutes,
      weeklyOvertimeMinutes: 0,
      overtimeMinutes,
      doubleTimeMinutes,
    });
  });

  // Pass 2: the weekly threshold counts all worked time. Daily overtime and
  // double-time minutes already occupy the tail of an entry, so subtract those
  // from the above-threshold tail before converting any ordinary minutes.
  const weeklyWorked = new Map<string, number>();

  ordered.forEach((entry) => {
    const allocation = result.get(entry.id)!;
    const weekKey = getWeekStartDateKey(entry.dateKey, entry.timeZone, rules.weekStartWeekday);
    const priorThisWeek = weeklyWorked.get(weekKey) ?? 0;
    const runningTotal = priorThisWeek + entry.paidMinutes;
    weeklyWorked.set(weekKey, runningTotal);

    const aboveWeeklyTail = Math.max(
      0,
      runningTotal - Math.max(priorThisWeek, rules.weeklyThresholdMinutes),
    );
    const alreadyPremiumTail = allocation.dailyOvertimeMinutes + allocation.doubleTimeMinutes;
    const ordinaryAboveWeekly = Math.max(0, aboveWeeklyTail - alreadyPremiumTail);
    const converted = Math.min(ordinaryAboveWeekly, allocation.ordinaryMinutes);

    if (converted > 0) {
      allocation.ordinaryMinutes -= converted;
      allocation.weeklyOvertimeMinutes += converted;
      allocation.overtimeMinutes += converted;
    }
  });

  return result;
}

/** Statutory annual leave accrued from payable hours (Ireland: 8%). */
export function accruedLeaveHours(payableMinutes: number, accrualPercent: number): number {
  return Math.round(((payableMinutes / 60) * accrualPercent) * 1000) / 1000;
}

export interface MinutePayBuckets {
  ordinaryMinutes: number;
  dailyOvertimeMinutes: number;
  weeklyOvertimeMinutes: number;
  doubleTimeMinutes: number;
  sundayMinutes: number;
  publicHolidayMinutes: number;
}

export interface MinutePayRates {
  baseHourlyRate: number;
  overtimeDailyMultiplier: number;
  overtimeWeeklyMultiplier: number;
  doubleTimeMultiplier: number;
  sundayPremiumMultiplier: number;
  publicHolidayMultiplier: number;
}

export interface MinutePayBreakdown {
  ordinaryPay: number;
  overtimePay: number;
  doubleTimePay: number;
  sundayPremiumPay: number;
  publicHolidayPremiumPay: number;
}

/**
 * Calculate minute-based earnings before display-hour rounding.
 *
 * A one-minute bucket is exactly 1/60 of an hour. Converting it through the
 * two-decimal display representation first can materially overpay short
 * buckets, so only the final monetary component is rounded to cents.
 */
export function calculateMinuteBasedPay(
  buckets: MinutePayBuckets,
  rates: MinutePayRates,
): MinutePayBreakdown {
  const pay = (minutes: number, multiplier = 1) => (
    (minutes / 60) * rates.baseHourlyRate * multiplier
  );

  return {
    ordinaryPay: roundMoney(pay(buckets.ordinaryMinutes)),
    overtimePay: roundMoney(
      pay(buckets.dailyOvertimeMinutes, rates.overtimeDailyMultiplier)
      + pay(buckets.weeklyOvertimeMinutes, rates.overtimeWeeklyMultiplier),
    ),
    doubleTimePay: roundMoney(pay(buckets.doubleTimeMinutes, rates.doubleTimeMultiplier)),
    sundayPremiumPay: roundMoney(pay(buckets.sundayMinutes, rates.sundayPremiumMultiplier)),
    publicHolidayPremiumPay: roundMoney(
      pay(buckets.publicHolidayMinutes, rates.publicHolidayMultiplier),
    ),
  };
}
